import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useServices } from '@/contexts/app-services';
import {
  prepareChatMessage,
  type ChatMessage,
  type ToolCall,
} from '@/entities/chat-message';
import { logError } from '@/services/logger';

export const messageKeys = {
  byConversationId: (id: string) => ['messages', id] as const,
};

interface UseAiMessagesParams {
  conversationId: string;
}

/**
 * Reads AI chat messages for a conversation from localStorage.
 * This is the data layer for message persistence.
 */
export function useAiMessages({ conversationId }: UseAiMessagesParams) {
  const { localStorage } = useServices();

  return useQuery<ChatMessage[]>({
    queryKey: messageKeys.byConversationId(conversationId),
    queryFn: () => localStorage.readMessages(conversationId),
    initialData: [], // prevent undefined state
  });
}

function useMessageUpdater({
  conversationId,
}: {
  conversationId: string;
}): (
  updater: (prev: ChatMessage[]) => ChatMessage[],
) => ChatMessage[] | undefined {
  const queryClient = useQueryClient();
  const { localStorage } = useServices();

  return useCallback(
    (updater: (prev: ChatMessage[]) => ChatMessage[]) => {
      const next = queryClient.setQueryData<ChatMessage[]>(
        messageKeys.byConversationId(conversationId),
        (prev = []) => updater(prev),
      );
      if (next) {
        localStorage.upsertMessages(conversationId, next);
      }
      return next;
    },
    [queryClient, conversationId, localStorage],
  );
}

interface UseSendMessagesParams {
  conversationId: string;
}

interface SendEventVariables {
  eventType: string;
  context: object;
}

interface SendMessageVariables {
  content: string;
  context: object;
}

function markRevisedSuggestions(messages: ChatMessage[]): ChatMessage[] {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage || lastMessage.role !== 'assistant') {
    return messages;
  }

  const newParentGoalIds = new Set(
    lastMessage.blocks
      .filter((b) => b.type === 'entity' && b.status === 'suggested')
      .map((b) => (b.type === 'entity' ? b.data.parentGoalId : null))
      .filter((id): id is string => id !== null && id !== undefined),
  );

  if (newParentGoalIds.size === 0) {
    return messages;
  }

  return messages.map((message) => {
    if (message === lastMessage || message.role !== 'assistant') {
      return message;
    }

    const hasMatchingBlock = message.blocks.some(
      (block) =>
        block.type === 'entity' &&
        block.status === 'suggested' &&
        block.data.parentGoalId != null &&
        newParentGoalIds.has(block.data.parentGoalId),
    );

    if (!hasMatchingBlock) {
      return message;
    }

    return {
      ...message,
      blocks: message.blocks.map((block) => {
        if (
          block.type === 'entity' &&
          block.status === 'suggested' &&
          block.data.parentGoalId != null &&
          newParentGoalIds.has(block.data.parentGoalId)
        ) {
          return { ...block, status: 'revised' as const };
        }
        return block;
      }),
    };
  });
}

/**
 * Handles sending messages and events to the AI service.
 * Manages optimistic updates, cache invalidation, and localStorage persistence.
 * This is the messaging mechanics layer - use useGoalsCompanion for business context.
 */
export function useSendMessages({ conversationId }: UseSendMessagesParams) {
  const queryClient = useQueryClient();
  const updateMessages = useMessageUpdater({ conversationId });
  const { ai } = useServices();

  const sendEvent = useMutation({
    mutationKey: ['sendEvent', conversationId],
    mutationFn: async ({ context }: SendEventVariables) => {
      const currentMessages =
        queryClient.getQueryData<ChatMessage[]>(
          messageKeys.byConversationId(conversationId),
        ) || [];

      const { chatMessageInput } = await ai.sendMessage({
        conversationId,
        messages: currentMessages,
        context,
      });

      return prepareChatMessage(chatMessageInput);
    },
    onMutate: async ({ eventType }: SendEventVariables) => {
      await queryClient.cancelQueries({
        queryKey: messageKeys.byConversationId(conversationId),
      });

      const previousMessages =
        queryClient.getQueryData<ChatMessage[]>(
          messageKeys.byConversationId(conversationId),
        ) || [];

      const optimisticEventMessage = prepareChatMessage({
        conversationId,
        role: 'user',
        blocks: [{ type: 'text', data: `Event: ${eventType}` }],
      });

      updateMessages((prev) => [...prev, optimisticEventMessage]);

      return { previousMessages };
    },
    onSuccess: (assistantMessage) => {
      updateMessages((prev) =>
        markRevisedSuggestions([...prev, assistantMessage]),
      );
    },
    onError: (_err, _variables, context) => {
      if (context?.previousMessages) {
        updateMessages(() => context.previousMessages);
      }
    },
  });

  const sendMessage = useMutation({
    mutationKey: ['sendMessage', conversationId],
    mutationFn: async ({
      context,
    }: SendMessageVariables): Promise<{
      message: ChatMessage;
      toolCalls: ToolCall[];
    }> => {
      const currentMessages =
        queryClient.getQueryData<ChatMessage[]>(
          messageKeys.byConversationId(conversationId),
        ) || [];

      const { chatMessageInput, toolCalls } = await ai.sendMessage({
        conversationId,
        messages: currentMessages,
        context,
      });

      return {
        message: prepareChatMessage(chatMessageInput),
        toolCalls,
      };
    },
    onMutate: async ({ content }: SendMessageVariables) => {
      await queryClient.cancelQueries({
        queryKey: messageKeys.byConversationId(conversationId),
      });

      const optimisticUserMessage = prepareChatMessage({
        conversationId,
        role: 'user',
        blocks: [{ type: 'text', data: content }],
      });

      updateMessages((prev) => [...prev, optimisticUserMessage]);
    },
    onError: (err) => {
      logError(err, 'Failed to send AI message');
    },
  });

  const resendConversation = useMutation({
    mutationKey: ['resendConversation', conversationId],
    mutationFn: async ({
      context,
    }: {
      context: object;
    }): Promise<{
      message: ChatMessage;
      toolCalls: ToolCall[];
    }> => {
      const existingMessages =
        queryClient.getQueryData<ChatMessage[]>(
          messageKeys.byConversationId(conversationId),
        ) || [];

      const { chatMessageInput, toolCalls } = await ai.sendMessage({
        conversationId,
        messages: existingMessages,
        context,
      });

      return {
        message: prepareChatMessage(chatMessageInput),
        toolCalls,
      };
    },
  });

  function persistAssistantMessage(assistantMessage: ChatMessage): void {
    updateMessages((prev) =>
      markRevisedSuggestions([...prev, assistantMessage]),
    );
  }

  return {
    isLoading:
      sendMessage.isPending ||
      sendEvent.isPending ||
      resendConversation.isPending,
    sendMessage,
    sendEvent,
    resendConversation,
    persistAssistantMessage,
  };
}

interface UseUpdateBlockStatusParams {
  conversationId: string;
}

export function useUpdateBlockStatus({
  conversationId,
}: UseUpdateBlockStatusParams): (
  messageId: string,
  blockIndex: number,
  status: 'added' | 'suggested' | 'skipped' | 'revised',
) => void {
  const updateMessages = useMessageUpdater({ conversationId });

  return useCallback(
    (
      messageId: string,
      blockIndex: number,
      status: 'added' | 'suggested' | 'skipped' | 'revised',
    ) => {
      updateMessages((prev) =>
        prev.map((message) => {
          if (message.id !== messageId) {
            return message;
          }
          return {
            ...message,
            blocks: message.blocks.map((block, i) => {
              if (i !== blockIndex || block.type !== 'entity') {
                return block;
              }
              return { ...block, status };
            }),
          };
        }),
      );
    },
    [updateMessages],
  );
}
